import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import { Workspace } from '@rossum/api-client/workspaces';
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
} from '@rossum/ui/x-data-grid-pro';
import { AnnotationLinkColumn } from '../components/columns/AnnotationLinkColumn';
import { DateTimeColumn } from '../components/columns/DateTimeColumn';
import { DetailToggle } from '../components/columns/DetailToggle';
import { ExecutionResultColumn } from '../components/columns/ExecutionResultColumn';
import { QueueColumn } from '../components/columns/QueueColumn';
import { RuleNameColumn } from '../components/columns/RuleNameColumn';
import { TextColumn } from '../components/columns/TextColumn';
import { TriggerResultColumn } from '../components/columns/TriggerResultColumn';
import { TransformedRuleExecutionLog } from '../helpers';

type UseColumnsProps = {
  queues: Queue[] | undefined;
  workspaces: Workspace[] | undefined;
};

export const useColumns = ({ queues, workspaces }: UseColumnsProps) => {
  const intl = useIntl();

  const columns: GridColDef<TransformedRuleExecutionLog>[] = [
    {
      field: 'executionResult',
      sortable: false,
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.columns.header.status',
      }),
      minWidth: 150,
      renderCell: ({ row }) => (
        <ExecutionResultColumn executionResult={row.executionResult} />
      ),
    },
    {
      field: 'ruleId',
      sortable: false,
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.columns.header.ruleId',
      }),
      minWidth: 100,
      flex: 1,
      renderCell: ({ row }) => <TextColumn value={`${row.ruleId}`} />,
    },
    {
      field: 'ruleName',
      sortable: false,
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.columns.header.ruleName',
      }),
      minWidth: 200,
      flex: 3,
      renderCell: ({ row }) => (
        <RuleNameColumn
          ruleId={row.ruleId}
          queueId={row.queueId}
          ruleName={row.ruleName}
        />
      ),
    },
    {
      field: 'triggerConditionResults',
      sortable: false,
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.columns.header.triggerResult',
      }),
      minWidth: 150,
      flex: 2,
      renderCell: ({ row }) => (
        <TriggerResultColumn
          triggerConditionResults={row.triggerConditionResults}
        />
      ),
    },
    {
      field: 'triggerEvent',
      sortable: false,
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.columns.header.triggerEvent',
      }),
      minWidth: 150,
      flex: 2,
      renderCell: ({ row }) => <TextColumn value={row.triggerEvent} />,
    },
    {
      field: 'createdAt',
      sortable: false,
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.columns.header.createdAt',
      }),
      minWidth: 200,
      flex: 3,
      renderCell: ({ row }) => <DateTimeColumn value={row.createdAt} />,
    },
    {
      field: 'queueId',
      sortable: false,
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.columns.header.queue',
      }),
      minWidth: 200,
      flex: 2,
      renderCell: ({ row }) => {
        const queue = queues?.find(q => q.id === row.queueId);
        const workspace = workspaces?.find(w => w.url === queue?.workspace);
        return (
          <QueueColumn
            queueName={queue?.name}
            workspaceName={workspace?.name}
          />
        );
      },
    },
    {
      field: 'annotationId',
      headerName: intl.formatMessage({
        id: 'features.rulesExecutionLogs.columns.header.annotationId',
      }),
      sortable: false,
      minWidth: 130,
      flex: 1,
      renderCell: ({ row }) => (
        <AnnotationLinkColumn annotationId={row.annotationId} />
      ),
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: params => <DetailToggle isExpanded={params.value} />,
    },
  ];

  return { columns };
};
